import React from 'react';
import { removeNodeAtPath } from 'react-sortable-tree';

import { CollapsiblePanel } from 'components/CollapsiblePanel';
import { CompanySortableTree } from './CompanySortableTree';
import { ProjectSortableTree } from './ProjectSortableTree';
import { cloneDeep, flatten } from 'lodash';

import {
	ContentWrapper,
	ColumnWrapper,
	MiddleContent,
} from './TaxonomyMapper.styles';
import { CompanyHeader } from './CompanyHeader';
import { ProjectHeader } from './ProjectHeader';
import { CompanyFooter } from './CompanyFooter';
import { ProjectFooter } from './ProjectFooter';
import {
	addNewL1Category,
	expandAll,
	getSortedTree,
	getNormalizedProjectTreeData,
	insertIntoRootNode,
	getNormalizedInsertedTreeNode,
	getTreesChangeData,
	markMappedItems,
	removeMappedItems,
	TREE_MENU_VALUES,
	iterateOverTree,
	getFilteredTree,
	getLeafNodesFromTree,
	removeCompanySpendItemsFromTree,
	getLeafIdsFromNode,
	getNodeKey,
	getCompanyTreeData,
	addNewCategoryToParent,
	addNewCategoryAsSybling,
	mergeTreeNodes,
	getConfirmationDialogMessage,
} from './utils';
import { getFormattedSpendWithSymbol } from 'utils/number';
import { getCookie, setCookie } from 'utils/cookies';
import { ConfirmationDialog } from './ConfirmationDialog';

const TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG = 'tax_mapper_do_not_show_dialog';

export class TaxonomyMapper extends React.Component {
	state = {
		isCompanyAResetModalOpen: false,
		isCompanyBResetModalOpen: false,
		isProjectResetModalOpen: false,
		confirmationDialogData: null,
	};

	componentDidUpdate() {
		const { needApplyHideToCompanyTrees } = this.state;

		let {
			companyATreeData,
			companyBTreeData,
			showMappedItems,
			onChange,
		} = this.props;

		if (needApplyHideToCompanyTrees) {
			if (!showMappedItems) {
				companyATreeData = removeMappedItems(companyATreeData);
				companyBTreeData = removeMappedItems(companyBTreeData);

				this.setState({
					needApplyHideToCompanyTrees: false,
				});

				onChange({
					companyATreeData,
					companyBTreeData,
				});
			}
		}
	}

	handleCompanyATreeChange = treeData => {
		this.props.onChange({ companyATreeData: treeData });
	};

	handleCompanyBTreeChange = treeData => {
		this.props.onChange({ companyBTreeData: treeData });
	};

	handleCompanyATreeSortChange = ({ field, direction }) => {
		let { companyATreeData } = this.props;

		companyATreeData = getSortedTree(companyATreeData, field, direction);

		this.props.onChange({ companyATreeData });
	};

	handleCompanyBTreeSortChange = ({ field, direction }) => {
		let { companyBTreeData } = this.props;

		companyBTreeData = getSortedTree(companyBTreeData, field, direction);

		this.props.onChange({ companyBTreeData });
	};

	handleProjectTreeSortChange = ({ field, direction }) => {
		let { projectTreeData } = this.props;

		projectTreeData = getSortedTree(projectTreeData, field, direction);

		this.props.onChange({ projectTreeData });
	};

	handleProjectTreeChange = treeData => {
		let stateUpdates = { projectTreeData: treeData };

		if (stateUpdates.projectTreeData) {
			stateUpdates = this.normalizeTreeDataBeforeChange(stateUpdates);
			const { changeData } = stateUpdates;

			const doNotShowDialog = getCookie(TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG);

			if (!doNotShowDialog && changeData) {
				delete stateUpdates.changeData; // no need to store it in the state

				const confirmationDialogMessage = getConfirmationDialogMessage(
					changeData
				);

				if (confirmationDialogMessage) {
					this.setState({
						confirmationDialogData: {
							pendingStateUpdates: stateUpdates,
							message: confirmationDialogMessage,
						},
					});

					return;
				}
			}

			this.delayedApplyHideToCompanyTrees();
		}

		this.props.onChange(stateUpdates);
	};

	delayedApplyHideToCompanyTrees = () => {
		// allow drop action to finish
		// delay fixes `Uncaught Error: No node found at the given path.` issue
		setTimeout(() => {
			if (!this.unmounted) {
				this.setState({ needApplyHideToCompanyTrees: true });
			}
		}, 1);
	};

	handleConfirmationDialogConfirm = doNotShowConfirmDialog => {
		const {
			confirmationDialogData: { pendingStateUpdates: stateUpdates },
		} = this.state;
		const { onChange } = this.props;

		this.delayedApplyHideToCompanyTrees();

		this.setState({ confirmationDialogData: null });

		onChange(stateUpdates);

		if (doNotShowConfirmDialog) {
			setCookie(TAX_MAPPER_DO_NOT_SHOW_CONFIRM_DIALOG, doNotShowConfirmDialog);
		}
	};

	handleConfirmationDialogClose = () => {
		this.setState({ confirmationDialogData: null });
	};

	normalizeTreeDataBeforeChange = ({ projectTreeData }) => {
		let {
			companyATreeData,
			companyBTreeData,
			projectTreeData: prevProjectTreeData,
		} = this.props;

		const changeData = getTreesChangeData(prevProjectTreeData, projectTreeData);

		if (!changeData) {
			return { projectTreeData };
		}

		companyATreeData = markMappedItems(companyATreeData, projectTreeData);
		companyBTreeData = markMappedItems(companyBTreeData, projectTreeData);
		changeData.parentNodeLevel = changeData.parentNode
			? changeData.parentNode.level
			: 0;

		if (changeData.parentNode) {
			let insertedNode = changeData.parentNode.children[changeData.index];
			changeData.insertedNodeLevel = insertedNode.level;
			changeData.parentNode.children[changeData.index] = { children: [] };

			insertedNode = getNormalizedInsertedTreeNode(
				insertedNode,
				changeData.parentNode
			);

			changeData.parentNode.children[changeData.index] = insertedNode;
			changeData.parentNode.children = flatten(changeData.parentNode.children);
			changeData.insertedNode = insertedNode;
		} else {
			let insertedNode = projectTreeData[changeData.index];
			changeData.insertedNodeLevel = insertedNode.level;
			projectTreeData.splice(changeData.index, 1);

			insertedNode = getNormalizedInsertedTreeNode(
				insertedNode,
				changeData.parentNode
			);

			projectTreeData = insertIntoRootNode(insertedNode, projectTreeData);
			changeData.insertedNode = insertedNode;
		}

		projectTreeData = getNormalizedProjectTreeData(projectTreeData);

		return {
			projectTreeData,
			companyATreeData,
			companyBTreeData,
			changeData,
		};
	};

	handleCompanyAChange = option => {
		this.props.onChange({ companyA: option });
	};

	handleCompanyBChange = option => {
		this.props.onChange({ companyB: option });
	};

	handleCompanyASearch = searchQuery => {
		this.props.onChange({ companyASearch: searchQuery });
	};

	handleCompanyBSearch = searchQuery => {
		this.props.onChange({ companyBSearch: searchQuery });
	};

	handleProjectSearch = searchQuery => {
		this.props.onChange({ projectSearch: searchQuery });
	};

	handleAddNewCategory = () => {
		const { projectTreeData, onChange } = this.props;

		let updatedProjectTreeData = addNewL1Category(projectTreeData);

		updatedProjectTreeData = getNormalizedProjectTreeData(
			updatedProjectTreeData
		);

		onChange({ projectTreeData: updatedProjectTreeData });
	};

	toggleCompanyATree = shouldExpand => {
		const { companyATreeData } = this.props;

		this.props.onChange({
			companyATreeData: expandAll(companyATreeData, shouldExpand),
		});
	};

	toggleCompanyBTree = shouldExpand => {
		const { companyBTreeData } = this.props;

		this.props.onChange({
			companyBTreeData: expandAll(companyBTreeData, shouldExpand),
		});
	};

	toggleProjectTree = shouldExpand => {
		const { projectTreeData } = this.props;

		this.props.onChange({
			projectTreeData: expandAll(projectTreeData, shouldExpand),
		});
	};

	showCompanyAResetConfirmation = () => {
		this.setState({
			isCompanyAResetModalOpen: true,
			isCompanyBResetModalOpen: false,
			isProjectResetModalOpen: false,
		});
	};

	showCompanyBResetConfirmation = () => {
		this.setState({
			isCompanyBResetModalOpen: true,
			isCompanyAResetModalOpen: false,
			isProjectResetModalOpen: false,
		});
	};

	showProjectResetConfirmation = () => {
		this.setState({
			isCompanyBResetModalOpen: false,
			isCompanyAResetModalOpen: false,
			isProjectResetModalOpen: true,
		});
	};

	hideCompanyAResetConfirmation = () => {
		this.setState({ isCompanyAResetModalOpen: false });
	};

	hideCompanyBResetConfirmation = () => {
		this.setState({ isCompanyBResetModalOpen: false });
	};

	hideProjectResetConfirmation = () => {
		this.setState({ isProjectResetModalOpen: false });
	};

	handleCompanyAReset = () => {
		const {
			companyATreeData,
			projectTreeData,
			onChange,
			showMappedItems,
			companyA,
			treeData,
		} = this.props;
		const newProjectTreeData = removeCompanySpendItemsFromTree(
			projectTreeData,
			companyA.value
		);
		let stateUpdates = { projectTreeData: newProjectTreeData };

		if (showMappedItems) {
			stateUpdates.companyATreeData = markMappedItems(
				companyATreeData,
				stateUpdates.projectTreeData
			);
		} else {
			stateUpdates.companyATreeData = getCompanyTreeData({
				companyOption: companyA,
				treeData,
				projectTreeData: stateUpdates.projectTreeData,
				showMappedItems,
			});
		}

		onChange(stateUpdates);
		this.hideCompanyAResetConfirmation();
	};
	handleCompanyBReset = () => {
		const {
			companyB,
			companyBTreeData,
			projectTreeData,
			onChange,
			showMappedItems,
			treeData,
		} = this.props;
		const newProjectTreeData = removeCompanySpendItemsFromTree(
			projectTreeData,
			companyB.value
		);
		let stateUpdates = { projectTreeData: newProjectTreeData };

		if (showMappedItems) {
			stateUpdates.companyBTreeData = markMappedItems(
				companyBTreeData,
				stateUpdates.projectTreeData
			);
		} else {
			stateUpdates.companyBTreeData = getCompanyTreeData({
				companyOption: companyB,
				treeData,
				projectTreeData: stateUpdates.projectTreeData,
				showMappedItems,
			});
		}

		onChange(stateUpdates);
		this.hideCompanyBResetConfirmation();
	};

	handleProjectTreeMenuOptionSelect = (key, node, path, parentNode) => {
		const {
			projectTreeData,
			onChange,
			showMappedItems,
			onShowMappedItemsChange,
			getCompanyTreeDataStateUpdates,
		} = this.props;

		if (
			key === TREE_MENU_VALUES.mergeWithAbove ||
			key === TREE_MENU_VALUES.mergeWithBelow
		) {
			const indexInParentNodeChildren = parentNode.children.findIndex(
				item => item.id === node.id
			);
			const mergeNodeIndex =
				key === TREE_MENU_VALUES.mergeWithAbove
					? indexInParentNodeChildren - 1
					: indexInParentNodeChildren + 1;
			const mergeNode = parentNode.children[mergeNodeIndex];
			const mergeResultNode = mergeTreeNodes(node, mergeNode);

			const newProjectTreeData = cloneDeep(projectTreeData);
			iterateOverTree(newProjectTreeData, item => {
				if (item.id === parentNode.id) {
					item.children[mergeNodeIndex] = mergeResultNode;
					// remove target node
					item.children.splice(indexInParentNodeChildren, 1);
				}
			});

			// update projectTreeData
			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.createChild) {
			let newProjectTreeData = addNewCategoryToParent(projectTreeData, node);

			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.createSibling) {
			let newProjectTreeData = addNewCategoryAsSybling(projectTreeData, node);

			onChange({ projectTreeData: newProjectTreeData });
		} else if (key === TREE_MENU_VALUES.find) {
			const searchQuery = getLeafIdsFromNode(node);
			// showing mapped items
			if (!showMappedItems) {
				onShowMappedItemsChange();
			}

			setTimeout(() => {
				onChange({
					companyASearch: searchQuery,
					companyBSearch: searchQuery,
				});
			}, 1);
		} else if (key === TREE_MENU_VALUES.remove) {
			let newProjectTreeData = removeNodeAtPath({
				treeData: projectTreeData,
				path,
				getNodeKey,
			});

			newProjectTreeData = getNormalizedProjectTreeData(newProjectTreeData);
			let stateUpdates = { projectTreeData: newProjectTreeData };

			stateUpdates = getCompanyTreeDataStateUpdates(stateUpdates);

			onChange(stateUpdates);
		}
	};

	handleCompanyTreeMenuOptionSelect = (key, node) => {
		if (key === TREE_MENU_VALUES.find) {
			const searchQuery = getLeafIdsFromNode(node);
			this.props.onChange({ projectSearch: searchQuery });
		} else if (key === TREE_MENU_VALUES.remove) {
			const {
				companyATreeData,
				companyBTreeData,
				projectTreeData,
				onChange,
				companyA,
			} = this.props;

			const stateUpdates = {};
			let newProjectTreeData = [];

			if (node.level === 4) {
				newProjectTreeData = getFilteredTree(
					projectTreeData,
					item => item.id !== node.id
				);
			} else {
				const leafNodeIds = [];
				const trunkIds = [node.id];

				iterateOverTree(node.children, item => {
					if (
						(item.level === 4 || item.level === 5) &&
						item.children.length === 0
					) {
						leafNodeIds.push(item.id);
					} else {
						trunkIds.push(item.id);
					}
				});

				newProjectTreeData = getFilteredTree(
					projectTreeData,
					node =>
						!leafNodeIds.includes(node.id) &&
						!(trunkIds.includes(node.originalId) && node.children.length === 0),
					true // filter parent after filtering its children
				);
			}

			newProjectTreeData = getNormalizedProjectTreeData(newProjectTreeData);
			stateUpdates.projectTreeData = newProjectTreeData;

			if (node.companyName === companyA.value) {
				stateUpdates.companyATreeData = markMappedItems(
					companyATreeData,
					newProjectTreeData
				);
			} else {
				stateUpdates.companyBTreeData = markMappedItems(
					companyBTreeData,
					newProjectTreeData
				);
			}

			onChange(stateUpdates);
		}
	};

	handleProjectReset = () => {
		const { onChange, getCompanyTreeDataStateUpdates } = this.props;
		const newProjectTreeData = [];
		let stateUpdates = { projectTreeData: newProjectTreeData };

		stateUpdates = getCompanyTreeDataStateUpdates(stateUpdates);

		onChange(stateUpdates);
		this.hideProjectResetConfirmation();
	};

	getPreviewUrl = companyOption => {
		const path = `/taxonomy-preview?company=${encodeURIComponent(
			companyOption.value
		)}`;

		return path;
	};

	getPercentageMappedByCompany = companyName => {
		const { companiesOptions } = this.props;

		const companyObj = companiesOptions.find(
			({ value }) => value === companyName
		);

		if (!companyObj) {
			return null;
		}

		const { percentage = 0 } = companyObj;

		return percentage;
	};

	getCompanyMappedInfo = companyName => {
		const { companiesOptions, currencySymbol } = this.props;

		const companyObj = companiesOptions.find(
			({ value }) => value === companyName
		);

		if (!companyObj) {
			return null;
		}

		const { mappedSpend = 0, percentage = 0 } = companyObj;

		return `${getFormattedSpendWithSymbol(
			mappedSpend,
			currencySymbol
		).toUpperCase()}(${percentage}%) of spend mapped`;
	};

	render() {
		const {
			confirmationDialogData,
			isCompanyAResetModalOpen,
			isCompanyBResetModalOpen,
			isProjectResetModalOpen,
		} = this.state;
		const {
			companiesOptions,
			companyA,
			companyB,
			companyATreeData,
			companyBTreeData,
			projectTreeData,
			currencySymbol,
			companyASearch,
			companyBSearch,
			projectSearch,
		} = this.props;

		return (
			<React.Fragment>
				<ContentWrapper>
					<CollapsiblePanel
						title={companyA.label}
						description={this.getCompanyMappedInfo(companyA.value)}
					>
						<ColumnWrapper>
							<CompanyHeader
								options={companiesOptions}
								selectedItem={companyA}
								onChange={this.handleCompanyAChange}
								onSortChange={this.handleCompanyATreeSortChange}
								searchOptions={getLeafNodesFromTree(companyATreeData)}
								currencySymbol={currencySymbol}
								onSearch={this.handleCompanyASearch}
								previewUrl={this.getPreviewUrl(companyA)}
							/>
							<CompanySortableTree
								data={companyATreeData}
								onChange={this.handleCompanyATreeChange}
								searchQuery={companyASearch}
								onMenuOptionSelect={this.handleCompanyTreeMenuOptionSelect}
								currencySymbol={currencySymbol}
							/>
							<CompanyFooter
								onExpand={this.toggleCompanyATree}
								percentage={this.getPercentageMappedByCompany(companyA.value)}
								onReset={this.showCompanyAResetConfirmation}
							/>
						</ColumnWrapper>
					</CollapsiblePanel>
					<MiddleContent>
						<ProjectHeader
							onSortChange={this.handleProjectTreeSortChange}
							searchOptions={getLeafNodesFromTree(projectTreeData)}
							currencySymbol={currencySymbol}
							onSearch={this.handleProjectSearch}
							onAddNew={this.handleAddNewCategory}
						/>
						<ProjectSortableTree
							data={projectTreeData}
							onChange={this.handleProjectTreeChange}
							searchQuery={projectSearch}
							onMenuOptionSelect={this.handleProjectTreeMenuOptionSelect}
							currencySymbol={currencySymbol}
						/>
						<ProjectFooter
							onExpand={this.toggleProjectTree}
							onReset={this.showProjectResetConfirmation}
							companiesOptions={companiesOptions}
						/>
					</MiddleContent>
					<CollapsiblePanel
						title={companyB.label}
						description={this.getCompanyMappedInfo(companyB.value)}
						align="right"
					>
						<ColumnWrapper>
							<CompanyHeader
								options={companiesOptions}
								selectedItem={companyB}
								onChange={this.handleCompanyBChange}
								onSortChange={this.handleCompanyBTreeSortChange}
								searchOptions={getLeafNodesFromTree(companyBTreeData)}
								onSearch={this.handleCompanyBSearch}
								currencySymbol={currencySymbol}
								previewUrl={this.getPreviewUrl(companyB)}
							/>
							<CompanySortableTree
								data={companyBTreeData}
								onChange={this.handleCompanyBTreeChange}
								searchQuery={companyBSearch}
								onMenuOptionSelect={this.handleCompanyTreeMenuOptionSelect}
								currencySymbol={currencySymbol}
							/>
							<CompanyFooter
								onExpand={this.toggleCompanyBTree}
								percentage={this.getPercentageMappedByCompany(companyB.value)}
								onReset={this.showCompanyBResetConfirmation}
							/>
						</ColumnWrapper>
					</CollapsiblePanel>
				</ContentWrapper>
				{confirmationDialogData && (
					<ConfirmationDialog
						text={confirmationDialogData.message}
						onConfirm={this.handleConfirmationDialogConfirm}
						onClose={this.handleConfirmationDialogClose}
					/>
				)}

				{(isCompanyAResetModalOpen || isCompanyBResetModalOpen) && (
					<ConfirmationDialog
						text="Please note, company taxonomy mappings will be removed from the project taxonomy."
						title="Company taxonomy reset"
						suppressCheckbox
						onConfirm={
							isCompanyAResetModalOpen
								? this.handleCompanyAReset
								: this.handleCompanyBReset
						}
						onClose={
							isCompanyAResetModalOpen
								? this.hideCompanyAResetConfirmation
								: this.hideCompanyBResetConfirmation
						}
					/>
				)}

				{isProjectResetModalOpen && (
					<ConfirmationDialog
						text="Please note, all company taxonomy mappings will be removed from the project taxonomy."
						title="Project taxonomy reset"
						suppressCheckbox
						onConfirm={this.handleProjectReset}
						onClose={this.hideProjectResetConfirmation}
					/>
				)}
			</React.Fragment>
		);
	}
}

export default TaxonomyMapper;
